import Chip from '@mui/material/Chip'
import Icon from '@mui/material/Icon'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { pathOr } from 'ramda'
import React from 'react'

import AudioPlayer from '@/app/component/atom/audio-player'
import CountryFlag from '@/app/component/atom/flag'
import IconText from '@/app/component/layout/icon-text'
import { formatDate, formatDateTime, formatPhoneNumber, parsePhoneNumber } from '@/app/service/util/format'

type DisplayProps = {
  field: any
  icon?: string
  title?: string
  tz?: string
}

export const DISPLAY_MAP: Record<string, React.FC<DisplayProps>> = {
  hidden: () => <React.Fragment />,
  text: ({ field, icon, title = '' }) => {
    if (icon && field !== undefined && field !== '') {
      return (
        <IconText>
          <Icon>{icon}</Icon>
          <Typography component="span" title={title}>
            {field}
          </Typography>
        </IconText>
      )
    }
    return (
      <Typography component="span" title={title}>
        {field}
      </Typography>
    )
  },
  audio: ({ field }) => <AudioPlayer playId={field} source={field} />,
  date: ({ field, tz }) => (
    <IconText>
      <Icon>calendar_today</Icon>
      <Typography component="span">{formatDate(field, tz)}</Typography>
    </IconText>
  ),
  datetime: ({ field, tz }) => (
    <IconText>
      <Typography component="span">{formatDateTime(field, tz)}</Typography>
    </IconText>
  ),
  boolean: ({ field }) => {
    if (field === 'true') {
      return <SuccessIcon>done</SuccessIcon>
    }
    if (field === 'false') {
      return <ErrorIcon>clear</ErrorIcon>
    }
    return <Icon>remove</Icon>
  },
  tag: ({ field }) => <Chip label={field} />,
  tagList: ({ field = [] }) => (
    <React.Fragment>
      {field.map((tag: string, index: number) => (
        <Chip key={index} label={tag} />
      ))}
    </React.Fragment>
  ),
  phone: ({ field }) => {
    const { country = '' } = parsePhoneNumber(field)
    return (
      <IconText>
        <CountryFlag code={country} />
        <Typography component="span">{formatPhoneNumber(`+${field}`)}</Typography>
      </IconText>
    )
  },
}

type Props = {
  displayMap?: Record<string, any>
  field?: any
  name?: string
  specs?: any
  title?: string
}

const FieldStatic: React.FC<Props> = ({ displayMap = DISPLAY_MAP, field = {}, name, specs = {}, title = '' }) => {
  const Content = pathOr(displayMap.text, [specs.display], displayMap)
  return (
    <Content
      name={name}
      field={typeof field === 'string' ? field.replace(/\n/g, ' \u23CE ') : field}
      title={title}
      {...specs}
    />
  )
}

const SuccessIcon = styled(Icon)(({ theme }) => ({
  color: theme.palette.success.main,
}))

const ErrorIcon = styled(Icon)(({ theme }) => ({
  color: theme.palette.error.main,
}))

export default FieldStatic
